import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSidebarItem } from "../config/sidebarConfig";
import { useAuth } from "../context/AuthContext";
import type { NoticeWithType } from "../components/ViewNotices";
import { canOpenSidebarItem } from "../domain/navigation/sidebarAccess";
import { searchCircles } from "../domain/askNoticeboard/circleSearch";
import { searchFiles } from "../domain/askNoticeboard/fileSearch";
import { searchHelp } from "../domain/askNoticeboard/helpSearch";
import { toHelpVideo } from "../domain/help/helpContent";
import { driveFiles, driveFolderOf } from "../domain/drive/driveFiles";
import { searchNotices } from "../domain/askNoticeboard/noticeSearch";
import { searchPages } from "../domain/askNoticeboard/pageSearch";
import { searchFamilies, searchUsers } from "../domain/askNoticeboard/peopleSearch";
import { toUsers } from "../domain/users/usersHandover";
import { accessibleSubcircles } from "../domain/circles/circleAccess";
import { circleHref, noticeHref, userHomeHref } from "../routes";
import { entitySearchIsUseful } from "../domain/askNoticeboard/entitySearch";
import { circlesQuery, familySearchIndexQuery, forViewer, noticesQuery, usersQuery, viewerIdOf } from "../services/queries";
import { toDriveSearch } from "../utils/driveNavigation";
import { useFeatureFlags } from "./useFeatureFlags";

/** One instant search result: what it is, its name, and where it opens. */
export type SearchResult = {
  key: string;
  kind: "Page" | "Notice" | "User" | "Family" | "Circle" | "File" | "Help article" | "Video";
  label: string;
  href: string;
  /** Router state for the page to read on arrival */
  state?: unknown;
};

export type InstantSearch = {
  results: SearchResult[];
  /** A name is being searched and an entity list has no data yet. */
  loading: boolean;
  /** An entity list failed, so Notices, people, Circles or files may be missing. */
  failed: boolean;
};

/**
 * Pages and help match as soon as they're typed: they need no download.
 * Notices, Users, families, Circles and Drive files reuse the same queries as
 * their pages. Those load only while the chat is open and the text can match
 * a name, once per list, not again on the next keystroke. A role that cannot
 * open a list never fetches it. Closing the chat stops enabling new loads.
 */
export function useInstantSearch(text: string, isOpen: boolean): InstantSearch {
  const { user, token } = useAuth();
  const { isFamilyManagementEnabled } = useFeatureFlags();
  const orgId = user?.schoolId || user?.orgId;
  const canOpenNotices = canOpenSidebarItem(getSidebarItem("notices"), user);
  const canOpenUsers = canOpenSidebarItem(getSidebarItem("users"), user);
  const canSearchFamilies = canOpenUsers && isFamilyManagementEnabled;
  const canOpenCircles = canOpenSidebarItem(getSidebarItem("circles"), user);
  const canOpenDrive = canOpenSidebarItem(getSidebarItem("drive"), user);
  const canOpenHelp = canOpenSidebarItem(getSidebarItem("helpCenter"), user);
  const wantEntities = isOpen && entitySearchIsUseful(text);
  const canLoadNotices = wantEntities && (canOpenNotices || canOpenDrive) && Boolean(user?.schoolId && token);
  const canLoadUsers = wantEntities && canOpenUsers && Boolean(orgId && token);
  const canLoadFamilies = wantEntities && canSearchFamilies && Boolean(orgId && token);
  const canLoadCircles = wantEntities && canOpenCircles && Boolean(user?.schoolId && token);
  // Drive's files come from the same Notices
  const noticesQueryResult = useQuery({
    ...forViewer(noticesQuery(user?.schoolId, token), viewerIdOf(user)),
    enabled: canLoadNotices,
  });
  const usersQueryResult = useQuery({
    ...forViewer(usersQuery(orgId, token), viewerIdOf(user)),
    enabled: canLoadUsers,
  });
  const childrenQueryResult = useQuery({
    ...forViewer(familySearchIndexQuery(orgId, token), viewerIdOf(user)),
    enabled: canLoadFamilies,
  });
  const circlesQueryResult = useQuery({
    ...forViewer(circlesQuery(user?.schoolId, token), viewerIdOf(user)),
    enabled: canLoadCircles,
  });
  const notices = noticesQueryResult.data;
  const users = usersQueryResult.data;
  const children = childrenQueryResult.data;
  const circles = circlesQueryResult.data;

  const results = useMemo(() => {
    const pages: SearchResult[] = searchPages(text, user).map(({ key, label, path }) => ({
      key,
      kind: "Page",
      label,
      href: path,
    }));
    // A Notice's page needs its noticeId, as from the Notices list.
    // Questions skip these lists, even when a page already cached them.
    const openable = wantEntities ? ((notices ?? []) as NoticeWithType[]).filter((n) => n.noticeId) : [];
    const found = wantEntities && canOpenNotices ? searchNotices(text, openable) : [];
    return [
      ...pages,
      ...found.map((notice) => ({
        key: `notice-${notice.noticeId}`,
        kind: "Notice" as const,
        label: notice.title,
        href: noticeHref(notice.noticeId),
        state: { notice },
      })),
      ...(wantEntities && canOpenUsers ? searchUsers(text, users ?? []) : []).map((found) => ({
        key: `user-${found.id}`,
        kind: "User" as const,
        label: found.name,
        href: userHomeHref(found.id),
      })),
      // A family opens Users searched for the child, which lists their guardians
      ...(wantEntities && canSearchFamilies ? searchFamilies(text, children ?? []) : []).map((child) => {
        const { pathname, state } = toUsers({ search: child });
        return { key: `family-${child}`, kind: "Family" as const, label: child, href: pathname, state };
      }),
      // Only the Circles this person manages, as the Circles page lists them
      ...(wantEntities && canOpenCircles ? searchCircles(text, accessibleSubcircles(circles ?? [], user)) : []).map((circle) => ({
        key: `circle-${circle.circleId}`,
        kind: "Circle" as const,
        label: circle.name,
        href: circleHref(circle.circleId),
      })),
      // A file opens Drive searched for it, in its folder
      ...(wantEntities && canOpenDrive ? searchFiles(text, driveFiles(notices, user?.schoolId)) : []).map(({ name, file }) => {
        const { pathname, state } = toDriveSearch(name, driveFolderOf(file, user?.schoolId));
        return { key: `file-${name}`, kind: "File" as const, label: name, href: pathname, state };
      }),
      // A video opens Video Tutorials playing it
      ...(canOpenHelp ? searchHelp(text) : []).map((found): SearchResult => {
        if (found.kind === "Article") {
          return { key: `help-${found.id}`, kind: "Help article", label: found.title, href: found.path };
        }
        const { pathname, state } = toHelpVideo(found.id);
        return { key: `video-${found.id}`, kind: "Video", label: found.title, href: pathname, state };
      }),
    ];
  }, [
    text,
    user,
    wantEntities,
    notices,
    canOpenNotices,
    users,
    canOpenUsers,
    children,
    canSearchFamilies,
    circles,
    canOpenCircles,
    canOpenDrive,
    canOpenHelp,
  ]);

  return {
    results,
    loading:
      noticesQueryResult.isLoading ||
      usersQueryResult.isLoading ||
      childrenQueryResult.isLoading ||
      circlesQueryResult.isLoading,
    failed:
      (canLoadNotices && noticesQueryResult.isError) ||
      (canLoadUsers && usersQueryResult.isError) ||
      (canLoadFamilies && childrenQueryResult.isError) ||
      (canLoadCircles && circlesQueryResult.isError),
  };
}
